import { ResponsiveDialog } from '@/components/responsiveDialog/ResponsiveDialog';
import { useOptOutPatientPharmacyReferral } from '@/hooks/order/useOptOutPatientPharmacyReferral';
import { Logger } from '@/utils/logger';
import { translateMontuError } from '@montugroup/circuit-shared';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  TextField,
  toast,
  Typography,
} from '@montugroup/design-system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material';
import { isErrorResponse } from '@ts-rest/core';
import { useState } from 'react';

export const REASON_MAX_LENGTH = 512;

const logger = new Logger('RequestDifferentLocationModal');

export function RequestDifferentLocationModal({
  open,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
}) {
  const theme = useTheme();
  const { optOut, isPending } = useOptOutPatientPharmacyReferral();
  const [reason, setReason] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleClose = () => {
    onClose();
    // NOTE: Reset after the close transition so the form doesn't visibly flash back while the dialog is still fading out
    setTimeout(() => {
      setReason('');
      setIsSubmitted(false);
    }, theme.transitions.duration.leavingScreen);
  };

  const handleSubmit = async () => {
    try {
      await optOut(reason.trim());
      setIsSubmitted(true);
      onSubmitted?.();
    } catch (error: unknown) {
      logger.error('Failed to opt out of pharmacy referral', error);

      if (isErrorResponse(error) && isMontuError(error)) {
        toast.error(
          translateMontuError({
            error: error.body.error,
            fallbackOverride: 'Failed to submit your request',
          }),
        );
        return;
      }

      toast.error('Failed to submit your request');
    }
  };

  if (isSubmitted) {
    return (
      <ResponsiveDialog
        open={open}
        onClose={handleClose}
        fullWidth
        title={
          <>
            <CheckCircleIcon color="success" />
            Request received
          </>
        }
      >
        <DialogContent>
          <DialogContentText>We&apos;ve received your request and will process it shortly.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" fullWidth onClick={handleClose}>
            Done
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    );
  }

  return (
    <ResponsiveDialog open={open} onClose={handleClose} fullWidth title="Request a different location">
      <DialogContent>
        <Stack gap={2}>
          <DialogContentText>
            Tell us why this pharmacy doesn&apos;t work for you. Our team will review your request.
          </DialogContentText>
          <TextField
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX_LENGTH))}
            multiline
            minRows={4}
            maxRows={4}
            fullWidth
            required
            slotProps={{
              htmlInput: { maxLength: REASON_MAX_LENGTH },
              formHelperText: { sx: { textAlign: 'right' } },
              inputLabel: { shrink: true },
            }}
            helperText={`${reason.length}/${REASON_MAX_LENGTH}`}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            You can still continue with your order while we review this.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack gap={1} width="100%">
          <Button variant="contained" fullWidth disabled={!reason.trim() || isPending} onClick={handleSubmit}>
            Submit request
          </Button>
          <Button variant="text" fullWidth onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
    </ResponsiveDialog>
  );
}
